import { useEffect } from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { ChangeAlertCallbackType, ChangeLoaderCallbackType } from "../../Root";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FlexGrid, Typography, DefaultComponentProps } from "@albo-ui/core";
import { statusConstitutiveAct } from "../../../util/DataObject";
import { useCompanyRecord } from "./hooks/useCompanyRecord";

import InfoIcon from '@mui/icons-material/Info';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import moment from "moment";
moment.locale('es');

export type ModalCompanyRecordProps = {
    onClose: () => void;
    open: boolean;
    uuid: string;
    changeLoader: ChangeLoaderCallbackType;
    changeAlert: ChangeAlertCallbackType;
} & DefaultComponentProps;

export const ModalCompanyRecordDialog = ({ changeAlert, changeLoader, onClose, open, uuid }: ModalCompanyRecordProps) => {
    const { data, zPos, onHandelGetAct } = useCompanyRecord(changeAlert, changeLoader, onClose);

    const getIcon  = (key, color) => {
        const icons = {
           "match":  <CheckCircleOutlineIcon style={{ color: color, fontSize: "21px" }} />,
           "mismatch":  <ErrorOutlineOutlinedIcon style={{ color: color, fontSize: "21px" }} />,
           "fix":  <ContentPasteSearchOutlinedIcon style={{ color: color, fontSize: "21px" }} />,
           "unknown":  <DoDisturbOnOutlinedIcon style={{ color: color, fontSize: "21px" }} />
        };
        return icons[key]
    };

    const cardDetail = ({ label, type, value, pages, status, reason, priority, extraData }) => {
        const getAdress = () => {
            const addressAct = {
                street: "", ext_number: "No. ext.", int_number: "int.", neighborhood: "Colonia", 
                municipality: "", state: "", city: "Ciudad", country: "", zip_code: "C.P.",
            }
            let text = []
            Object.keys(addressAct).map((i) => extraData && extraData[i] ? text.push(addressAct[i] ? addressAct[i] + " " + extraData[i] : extraData[i]) : null)
            return text.length > 0 ? text.join(", ") : "No se ubicó el dato dentro del documento.";
        };
        const getAdmins = () => {
            if(extraData && extraData.length > 0){
                return extraData.map((i, index) => {
                    let name = i.name.toLowerCase().replace(/(?:^|\s)\S/g, char => char.toUpperCase());
                    return <Typography key={index} variant="smallTitle">{index+1}. {name} 
                        {i["share_percentage"] ? `- ${i["share_percentage"]}%` : null}
                        {i["tax_id"] ? ` - RFC: ${i["tax_id"]}` : null}
                        {i["position"] ? ` - Posición: ${i["position"]}` : null}
                        {i["faculty"] ? ` - Facultad: ${i["faculty"]}` : null}
                    </Typography>
                })
            } else {
                return "No se ubicó el dato dentro del documento."
            }
        };

        const getPages = () => {
            return extraData && extraData.length > 0 ?
                extraData.map((i, index) => {
                    return <Typography key={index} variant="paragraph">{"Pág. " + i.pages?.join(", ")}</Typography>
                })
            : "Sin coincidencia"
        };

        return (
            <Box display="flex" alignItems="center" justifyContent="space-between"
                sx={{borderBottom: "1px solid #E0E0E0", py: 1, width: "100%"}}
            >
                <Box display={"grid"} pr={1}>
                    <Typography variant="paragraph" style={{ mb: "4px" }}>{label}</Typography>
                    <Typography variant="smallTitle" style={{display: "contents"}}>
                    {   type === "date" ? moment.utc(value).format("LL") :
                        type === "object" ? getAdress() :
                        type === "array" ? getAdmins() : value ? value : "No se ubicó el dato dentro del documento."
                    }
                    </Typography>
                </Box>
                <Box display="grid" alignItems="center" gap={"4px"}>
                    {statusConstitutiveAct.map((s) => (
                        s.key === status && <Tooltip key={s.key} title={<Typography variant="smallTitle" color="white">{reason || "No aplica la comparación"}</Typography>} sx={{fontSize: 20}}>
                            <Chip key={s.key}
                                icon={getIcon(s.key, s.color)}
                                label={s.label} size="small"
                                sx={{px: 1, py: 2.5, minWidth: 145, color: s.color, backgroundColor: s.backgroundColor, fontSize: "16px", justifyContent: "left"}}
                            />
                        </Tooltip>
                    ))}
                <Typography variant="paragraph" style={{textAlign: "end", display: "inherit"}}>
                    { type !== "array" ? pages ? "Pág. " + pages.join(", ") : "Sin coincidencia" : getPages()}
                </Typography>
                </Box>
            </Box>
        );
    };

    useEffect(() => {
        if (uuid && open) {
            onHandelGetAct(uuid);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid, open]);

    
    return (
        <Dialog open={open} onClose={onClose} style={{ display: zPos !== 45 ? "none" : null, width: 730, maxWidth: "80vw", maxHeight: 600 }}>
            <DialogTitle>
                <Typography variant="h2">Acta Constitutiva</Typography>
            </DialogTitle>

            <DialogContent>
                {data && data.length > 0 ? 
                    <><InfoIcon fontSize='small' color='primary' sx={{ mr: 0.5, verticalAlign: "sub" }}/><Typography variant='paragraph' color='primary' style={{fontSize: 16}}>A continuación te mostramos los datos extraídos del acta.</Typography></>
                    :
                    <><InfoIcon fontSize='small' color='error' sx={{ mr: 0.5, verticalAlign: "sub" }}/><Typography variant='paragraph' color='required' style={{fontSize: 16}}>La extracción de datos y análisis del documento falló.</Typography></>
                }
                <FlexGrid container spacing={0} direction="column" style={{marginTop: 20}}>
                    {data && data.map((item, key) => (
                        <FlexGrid item container key={key}>{cardDetail(item)}</FlexGrid>
                    ))}
                </FlexGrid>
            </DialogContent>
                <DialogActions>
                    <FlexGrid item>
                        <Button onClick={onClose} buttonType="secondary" size="normal">Entendido</Button>
                    </FlexGrid>
                </DialogActions>
        </Dialog>
    );
};
